import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { lightTheme } from '@/customTheme'
import GradientLoader from '@/components/GradientLoader'
import GradientSpinner from '@/components/GradientSpinner/GradientSpinner'
import CheckIconSmall from '@/scenes/Editor/components/Icons/CheckIconSmall'
import {
  CARD_CHECKER_BACKGROUND,
  CARD_ERROR_OVERLAY_STYLE,
  CARD_INSET_BORDER,
  CARD_LABEL_STYLE,
  CARD_SELECTED_BADGE_STYLE,
  CARD_UNSELECTED_BADGE_STYLE,
  cardButtonClusterStyle,
  cardShellStyle,
  cardSurfaceStyle,
} from './GeneratedCard.styles'
import {
  BeforeAfterCardButton,
  DownloadCardButton,
  EditCardButton,
} from './GeneratedCardButtons'

/**
 * A holding surface for a single preview image, shared by the Photoshoots grid
 * and the Batch studio. The preview is composed of HTML layers — a background
 * plate (solid color / image / checker) with the foreground image on top — so
 * background changes are instant style swaps; the layers are flattened into one
 * image only at export time.
 */
export type GeneratedCardBackground =
  | { type: 'color'; value: string }
  | { type: 'image'; src: string }
  | { type: 'checker' }
  | { type: 'none' }

export type GeneratedCardLoaderStyle = 'gradient' | 'loader'

/** Slots for caller-supplied custom buttons, appended after the standard ones. */
export interface GeneratedCardExtraButtons {
  bottomLeft?: React.ReactNode[]
  bottomRight?: React.ReactNode[]
}

/** Download action config — the card renders a DownloadCardButton (popover) from it. */
export interface GeneratedCardDownload {
  /** Resolver for the popover's download scope (e.g. the exported data URL). */
  downloadScope: () => string[] | Promise<string[]>
  source?: string
  destination?: string
  tooltipText?: string
  defaultFormatKey?: string
  downloadButtonOpenPremiumPrompt?: string
  /** Premium source key: gates the card's download button itself — non-premium users see the premium modal instead of the popover. */
  openOnButtonClickPremiumPrompt?: string
  showWatermarkButton?: boolean
}

export interface GeneratedCardProps {
  /** Foreground preview image (result / cutout / original), layered over the background. */
  previewSrc?: string | null
  /** What sits behind a transparent preview. Defaults to white. */
  background?: GeneratedCardBackground
  /** Before image. When set, the card renders a before/after button and manages the press itself. */
  beforeSrc?: string | null
  alt?: string
  /** Optional caption rendered under the card. */
  label?: string
  /** Aspect ratio (w / h). Drives the loading placeholder and, when `fixedAspect`, the tile shape. */
  aspectRatio?: number
  /** When true the surface keeps `aspectRatio` and the image is `object-fit: contain`
   *  (tile look). When false the image flows at its natural height (photoshoot look). */
  fixedAspect?: boolean
  isLoading?: boolean
  loaderStyle?: GeneratedCardLoaderStyle
  /** Show the percentage readout on the gradient loader. */
  showLoaderPercentage?: boolean
  loaderExpectedDurationSeconds?: number
  /** Selected tile: gradient border + check badge. */
  selected?: boolean
  /** Override the active border width (px). Defaults to 4. */
  activeBorderWidth?: number
  /** Override the active border color. Defaults to the cyan→magenta gradient. */
  activeBorderColor?: string
  /** Override the shell corner radius (px). Defaults to 20. */
  cornerRadius?: number
  /** When provided, clicking the surface toggles selection. */
  onSelect?: (event: React.MouseEvent) => void
  /** Whether to show the selection check badge. Defaults to true. */
  showSelectionBadge?: boolean
  errorText?: string | null
  /** Click on the preview image (e.g. open in editor). */
  onPreviewClick?: () => void

  // --- Standard actions: the card builds and wires the buttons from these. ---
  /** When set, renders an "open in editor" button (and is also the preview-click target if `onPreviewClick` is unset). */
  onEdit?: () => void
  editTooltip?: string
  /** Edit button visual: 'blue-purple-glow' (default, AI-glow shadow) or 'plain' (flat black shadow — batch tiles). */
  editButtonVariant?: 'blue-purple-glow' | 'plain'
  /** When set, renders a download button that opens a DownloadPopover. */
  download?: GeneratedCardDownload
  /** Optional hooks fired when the internal before/after press starts / ends. */
  onBeforeAfterStart?: () => void
  onBeforeAfterEnd?: () => void

  /** Extra custom buttons appended after the standard ones (the "allow extension" path). */
  extraButtons?: GeneratedCardExtraButtons
  /** Reveal the buttons only while hovered (default true). When false they always show. */
  revealButtonsOnHover?: boolean
  /** Optional CSS filter applied to the preview image (e.g. drop-shadow for batch shadow). */
  previewFilter?: string
  /** Optional CSS transform applied to the preview image (e.g. bbox reframing on resize).
   *  transform-origin is centered to match the contain-fit centering. */
  previewTransform?: string
  /** Fixed-aspect foreground fitting. Batch Resize uses cover for Fill. */
  previewObjectFit?: 'contain' | 'cover'
  /** Caller-owned visual layer rendered above the preview and below card actions. */
  previewOverlay?: React.ReactNode
}

const WHITE_BACKGROUND: GeneratedCardBackground = { type: 'color', value: lightTheme.colors.white }

function resolveBackground(background: GeneratedCardBackground): React.CSSProperties {
  switch (background.type) {
    case 'color':
      return { background: background.value }
    case 'image':
      return {
        backgroundImage: `url(${background.src})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    case 'checker':
      return { background: CARD_CHECKER_BACKGROUND }
    case 'none':
    default:
      return { background: 'transparent' }
  }
}

function GeneratedCard({
  previewSrc,
  background = WHITE_BACKGROUND,
  beforeSrc,
  alt,
  label,
  aspectRatio = 1,
  fixedAspect = false,
  isLoading = false,
  loaderStyle = 'gradient',
  showLoaderPercentage = false,
  loaderExpectedDurationSeconds,
  selected = false,
  activeBorderWidth,
  activeBorderColor,
  cornerRadius,
  onSelect,
  showSelectionBadge = true,
  errorText,
  onPreviewClick,
  onEdit,
  editTooltip,
  editButtonVariant = 'blue-purple-glow',
  download,
  onBeforeAfterStart,
  onBeforeAfterEnd,
  extraButtons,
  revealButtonsOnHover = true,
  previewFilter,
  previewTransform,
  previewObjectFit = 'contain',
  previewOverlay,
}: GeneratedCardProps) {
  const { t } = useTranslation()
  const [isHovered, setIsHovered] = useState(false)
  const [isButtonPopoverOpen, setIsButtonPopoverOpen] = useState(false)
  const [showBefore, setShowBefore] = useState(false)

  const showHover = (isHovered || isButtonPopoverOpen) && !isLoading
  const active = selected || showHover
  const hasBeforeAfter = !!beforeSrc && !!previewSrc
  const isShowingBefore = showBefore && hasBeforeAfter
  const showButtons = !isLoading && (!revealButtonsOnHover || showHover) && !errorText

  // A fixed-aspect tile keeps its square box; a natural card only reserves the
  // aspect box while loading (so the spinner has room before the image lands).
  const enforceAspect = fixedAspect || isLoading
  const surfaceBackground = resolveBackground(background)

  // Clicking the preview falls back to the edit action only when the card is not
  // selectable. When `onSelect` is provided the click should bubble to the shell
  // so selection remains the primary tile interaction; edit is still accessible
  // via the hover button cluster.
  const previewClick = onPreviewClick ?? (onSelect ? undefined : onEdit)

  const handleBeforeStart = useCallback(() => {
    setShowBefore(true)
    onBeforeAfterStart?.()
  }, [onBeforeAfterStart])
  const handleBeforeEnd = useCallback(() => {
    setShowBefore(false)
    onBeforeAfterEnd?.()
  }, [onBeforeAfterEnd])

  // The card owns assembly of the standard buttons; extras are appended after.
  const bottomRight: React.ReactNode[] = [
    hasBeforeAfter ? (
      <BeforeAfterCardButton
        key="before-after"
        onPressStart={handleBeforeStart}
        onPressEnd={handleBeforeEnd}
      />
    ) : null,
    onEdit ? (
      <EditCardButton key="edit" onClick={onEdit} tooltipText={editTooltip} variant={editButtonVariant} />
    ) : null,
    // Extras sit between the standard edit action and download, which stays pinned closest to the corner.
    ...(extraButtons?.bottomRight ?? []),
    download ? (
      <DownloadCardButton
        key="download"
        downloadScope={download.downloadScope}
        source={download.source}
        destination={download.destination}
        tooltipText={download.tooltipText}
        defaultFormatKey={download.defaultFormatKey}
        downloadButtonOpenPremiumPrompt={download.downloadButtonOpenPremiumPrompt}
        openOnButtonClickPremiumPrompt={download.openOnButtonClickPremiumPrompt}
        showWatermarkButton={download.showWatermarkButton}
        onPopoverOpenChange={(isOpen) => {
          setIsButtonPopoverOpen(isOpen)
          if (!isOpen) setIsHovered(false)
        }}
      />
    ) : null,
  ].filter(Boolean)
  const bottomLeft: React.ReactNode[] = extraButtons?.bottomLeft ?? []

  const hasBottomLeft = bottomLeft.length > 0
  const hasBottomRight = bottomRight.length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      <div
        onMouseEnter={() => setIsHovered(true)}
        // Covers a card that appears under a still pointer (no mouseenter fires,
        // e.g. the Batch grid returning from full screen): first movement reveals it.
        onMouseMove={() => { if (!isHovered) setIsHovered(true) }}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onSelect && !isLoading ? onSelect : undefined}
        style={{
          ...cardShellStyle(active, { borderWidth: activeBorderWidth, borderColor: activeBorderColor, borderRadius: cornerRadius }),
          cursor: onSelect && !isLoading ? 'pointer' : undefined,
        }}
      >
        <div
          style={{
            ...cardSurfaceStyle(activeBorderWidth, cornerRadius),
            ...surfaceBackground,
            ...(enforceAspect ? { aspectRatio: `${aspectRatio} / 1` } : {}),
          }}
        >
          {isLoading ? (
            loaderStyle === 'gradient' ? (
              <GradientLoader
                angle={0}
                borderRadius={Math.max((cornerRadius ?? 20) - (activeBorderWidth ?? 4), 0)}
                fillContainer
                showPercentage={showLoaderPercentage}
                expectedDurationSeconds={loaderExpectedDurationSeconds}
              />
            ) : (
              <>
                {previewSrc ? (
                  <img
                    src={previewSrc}
                    alt={alt}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: previewObjectFit,
                      display: 'block',
                      filter: previewFilter,
                      transform: previewTransform,
                      transformOrigin: 'center',
                    }}
                    draggable={false}
                  />
                ) : null}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'inherit',
                  }}
                >
                  <GradientSpinner fill="white" style={{ flex: 'none' }} size={40} />
                </div>
              </>
            )
          ) : (
            <>
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt={alt}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onClick={
                    previewClick && !isShowingBefore
                      ? (e) => {
                          // Don't let the preview click bubble into the tile's select handler.
                          e.stopPropagation()
                          previewClick()
                        }
                      : undefined
                  }
                  style={
                    fixedAspect
                      ? {
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: previewObjectFit,
                          display: 'block',
                          opacity: isShowingBefore ? 0 : 1,
                          cursor: previewClick ? 'pointer' : undefined,
                          filter: previewFilter,
                          transform: previewTransform,
                          transformOrigin: 'center',
                        }
                      : {
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          opacity: isShowingBefore ? 0 : 1,
                          cursor: previewClick ? 'pointer' : undefined,
                          filter: previewFilter,
                          transform: previewTransform,
                          transformOrigin: 'center',
                        }
                  }
                />
              ) : null}

              {isShowingBefore ? (
                <img
                  src={beforeSrc!}
                  alt={alt}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                  }}
                />
              ) : null}

              {!isShowingBefore ? previewOverlay : null}

              {errorText ? <div style={CARD_ERROR_OVERLAY_STYLE}>{errorText}</div> : null}

              {showButtons && hasBottomLeft ? (
                <div style={cardButtonClusterStyle('left')} onClick={(e) => e.stopPropagation()}>
                  {bottomLeft}
                </div>
              ) : null}

              {showButtons && hasBottomRight ? (
                <div style={cardButtonClusterStyle('right')} onClick={(e) => e.stopPropagation()}>
                  {bottomRight}
                </div>
              ) : null}
            </>
          )}

          {showSelectionBadge && (selected || (onSelect && showHover && !isLoading)) ? (
            <div
              style={{
                ...(selected ? CARD_SELECTED_BADGE_STYLE : CARD_UNSELECTED_BADGE_STYLE),
                ...(onSelect && !isLoading ? { cursor: 'pointer' } : {}),
              }}
              onClick={
                onSelect && !isLoading
                  ? (e) => {
                      e.stopPropagation()
                      onSelect(e)
                    }
                  : undefined
              }
            >
              {selected ? <CheckIconSmall fill={lightTheme.colors.white} /> : null}
            </div>
          ) : null}

          {!active ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                boxShadow: CARD_INSET_BORDER,
                pointerEvents: 'none',
              }}
            />
          ) : null}
        </div>
      </div>

      {label && !isLoading ? <p style={CARD_LABEL_STYLE}>{t(label)}</p> : null}
    </div>
  )
}

export default GeneratedCard
